// Közös TÖBB-választós entitás-picker — SZINKRON opció-listával (kis referencialisták) + kereső + checkbox.
// Az EntityCombobox vizuális mintáját követi (Popover + kereső + lista), de több-választós. Kontrollált:
// `value` az id-tömb, `onChange(ids)`. A kiválasztás a popover zárása NÉLKÜL bővíthető. A feliratokat a hívó
// adja (i18n-agnosztikus) — így bármely modul (számla fiók-scope, üzenet-címzés) újrahasználhatja.
import { matchesSearch } from '@atlas/shared';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { CheckboxVisual } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  id: string;
  name: string;
  /**
   * OPCIONÁLIS másodlagos nevek (pl. a kapcsolattartó hotelei, a hívó rendezi): a sorban „Név (első +N)" alakban
   * látszanak, és mindegyikre lehet keresni — kereséskor a TALÁLÓ áll elöl. A triggerben csak a `name` szerepel.
   */
  secondary?: string[] | undefined;
}

/** A sorban megjelenő másodlagos név: kereséskor az első találó, egyébként a lista első eleme. */
function shownSecondary(secondary: string[], query: string): string | undefined {
  if (query.trim() !== '') {
    const hit = secondary.find((s) => matchesSearch(s, query));
    if (hit !== undefined) return hit;
  }
  return secondary[0];
}

/**
 * Opció-CSOPORT (pl. részleg → csapat): a fejléce egy kattintással a csoport ÖSSZES opcióját ki-/bejelöli. A
 * `children` nélküli (levél) csoport alatt a tagjai látszanak; a gyerekes csoport alatt a gyerek-csoportok. A
 * kiválasztás továbbra is opció-id-k listája — a csoport csak a választást segíti, értékként nem jelenik meg.
 */
export interface MultiSelectGroup {
  id: string;
  name: string;
  /** a csoport ÖSSZES opciója (gyerekes csoportnál a gyerekek uniója) — csak a `options`-ban szereplő id számít */
  optionIds: string[];
  children?: MultiSelectGroup[] | undefined;
}

export interface EntityMultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  loading?: boolean | undefined;
  /** A kiválasztás-számláló felirata (count-interpolációval); ha hiányzik, csak a nevek látszanak. */
  countLabel?: ((count: number) => string) | undefined;
  /**
   * KISBETŰSÍTI az opció-feliratokat és a triggert. A SZŰRŐ-használathoz (enum-címkék: státusz, kategória) —
   * a rendszer szűrő-legördülői kisbetűsek. Tulajdonnév-listáknál (fiók, kapcsolattartó, szolgáltatás) NEM
   * szabad bekapcsolni: ott a saját írásmód a helyes.
   */
  lowercaseOptions?: boolean | undefined;
  /**
   * Az ÜRES állapot felirata (a `placeholder`, pl. „mind") kisbetűs — a szűrő-konvenció: a „mind" akkor is kisbetűs,
   * ha az opciók TULAJDONNEVEK (`lowercaseOptions` nélkül). A kiválasztott nevek írásmódját nem érinti.
   */
  lowercasePlaceholder?: boolean | undefined;
  /**
   * OPCIONÁLIS csoportosítás (alapból KI — a többi hívó változatlan, lapos listát kap). Megadva a lista
   * csoport-fejlécekkel jelenik meg; kereséskor a névre illeszkedő csoportok a találatok ELÉ kerülnek (a fejlécük
   * ugyanúgy kijelöl). A csoportba nem tartozó opciók a lista végén, az `ungroupedLabel` alatt.
   */
  groups?: MultiSelectGroup[] | undefined;
  /** a csoportba nem tartozó opciók fejléce (csak `groups` mellett) */
  ungroupedLabel?: string | undefined;
  'aria-label'?: string | undefined;
}

/**
 * A fa-szint jelölése: szintenként egy VEZETŐVONAL (a `SelectItem` `depth`-ének mintája — a repo fa-konvenciója),
 * nem puszta behúzás és nem írásjel a névben.
 */
function DepthGuides({ depth }: { depth: number }): JSX.Element[] {
  return Array.from({ length: Math.max(0, depth) }, (_, i) => (
    <span key={i} aria-hidden className="bg-border ml-1 mr-0.5 h-4 w-px shrink-0" />
  ));
}

export function EntityMultiSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  countLabel,
  lowercaseOptions,
  lowercasePlaceholder,
  groups,
  ungroupedLabel,
  'aria-label': ariaLabel,
}: EntityMultiSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // ZÁRÁSKOR a keresőszöveg ürül: különben az újranyitott lista a régi szűréssel nyílt, és úgy tűnt, a többi
  // elem eltűnt (kolléga-észrevétel). Nyitva tartott többválasztásnál viszont MARAD — egy keresésre több
  // találat is egymás után kipipálható.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const selected = useMemo(() => new Set(value), [value]);
  // A szűrés ÉKEZET-ÉRZÉKETLEN (a közös `matchesSearch`): a magyar felhasználó gyakran ékezet nélkül gépel
  // a keresőbe („panzio" → „Panzió"), és egy 475 elemű kapcsolattartó-listán ez a különbség a használhatóság.
  const filtered = useMemo(
    () =>
      query.trim() === ''
        ? options
        : options.filter(
            (o) =>
              matchesSearch(o.name, query) ||
              (o.secondary ?? []).some((s) => matchesSearch(s, query)),
          ),
    [options, query],
  );

  const selectedNames = useMemo(
    () => options.filter((o) => selected.has(o.id)).map((o) => o.name),
    [options, selected],
  );
  const triggerLabel = selectedNames.length === 0 ? placeholder : selectedNames.join(', ');

  const toggle = (id: string): void => {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  // ── csoportosítás (csak `groups` mellett) ──
  const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const searching = query.trim() !== '';
  // a lapos csoport-lista a kereséshez (szülő + gyerek egyaránt, a fában elfoglalt helyük nélkül)
  const matchedGroups = useMemo(() => {
    if (!groups || !searching) return [];
    const out: MultiSelectGroup[] = [];
    const walk = (list: MultiSelectGroup[]): void => {
      for (const g of list) {
        if (matchesSearch(g.name, query)) out.push(g);
        if (g.children) walk(g.children);
      }
    };
    walk(groups);
    return out;
  }, [groups, searching, query]);
  const ungrouped = useMemo(() => {
    if (!groups) return [];
    const inGroup = new Set(groups.flatMap((g) => g.optionIds));
    return options.filter((o) => !inGroup.has(o.id));
  }, [groups, options]);

  const groupIds = (g: MultiSelectGroup): string[] =>
    g.optionIds.filter((id) => optionById.has(id));
  const toggleGroup = (g: MultiSelectGroup): void => {
    const ids = groupIds(g);
    if (ids.length > 0 && ids.every((id) => selected.has(id))) {
      const drop = new Set(ids);
      onChange(value.filter((v) => !drop.has(v)));
    } else {
      onChange([...value, ...ids.filter((id) => !selected.has(id))]);
    }
  };

  // a hotelnév RÖVIDÜL (a név nem), a „+N)" sosem vágódik le — enélkül a hosszú sorokon épp a „még N hotel"
  // jelzés tűnt el (mérve 288 px-es listán)
  // a másodlagos rész SZÖVEGE: „(Hotel +N)" — a sor akadálymentes neve ebből áll össze (a vizuális bontás
  // flex-darabjai közé a böngésző szóközt tenne: „( Hotel")
  const secondaryText = (opt: MultiSelectOption): string | null => {
    const list = opt.secondary ?? [];
    const shown = shownSecondary(list, query);
    if (shown === undefined) return null;
    return `(${shown}${list.length > 1 ? ` +${String(list.length - 1)}` : ''})`;
  };

  const secondaryLabel = (opt: MultiSelectOption): JSX.Element | null => {
    const list = opt.secondary ?? [];
    const shown = shownSecondary(list, query);
    if (shown === undefined) return null;
    return (
      <span className="text-muted-foreground flex min-w-0">
        <span className="whitespace-pre"> (</span>
        <span className="truncate">{shown}</span>
        <span className="shrink-0 whitespace-pre">
          {list.length > 1 ? ` +${String(list.length - 1)})` : ')'}
        </span>
      </span>
    );
  };

  const optionRow = (opt: MultiSelectOption, depth = 0, key = opt.id): JSX.Element => {
    const checked = selected.has(opt.id);
    const secondary = secondaryText(opt);
    return (
      <button
        key={key}
        type="button"
        role="option"
        aria-selected={checked}
        aria-label={secondary === null ? undefined : `${opt.name} ${secondary}`}
        onClick={() => toggle(opt.id)}
        className="hover:bg-accent flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm"
      >
        <DepthGuides depth={depth} />
        {/* CSAK jelző (nem vezérlő): a kattintást a sor-gomb kezeli. Interaktív Radix-checkbox itt
            `<button>`-t ültetett `<button>`-be — érvénytelen DOM, és végtelen render-ciklust okozott. */}
        <CheckboxVisual checked={checked} />
        <span className="flex min-w-0">
          {/* a NÉV az elsődleges: nem zsugorodik a hotel kedvéért, csak ha egymagában sem fér ki (max-w-full) */}
          <span
            className={cn('max-w-full shrink-0 truncate', lowercaseOptions === true && 'lowercase')}
          >
            {opt.name}
          </span>
          {secondaryLabel(opt)}
        </span>
      </button>
    );
  };

  // a csoport-fejléc NEM `option` (az a tagoké): kapcsoló-gomb, a köztes állapot `aria-pressed="mixed"`
  const groupRow = (g: MultiSelectGroup, depth: number, key = g.id): JSX.Element => {
    const ids = groupIds(g);
    const count = ids.filter((id) => selected.has(id)).length;
    const all = ids.length > 0 && count === ids.length;
    return (
      <button
        key={key}
        type="button"
        aria-pressed={all ? true : count > 0 ? 'mixed' : false}
        onClick={() => toggleGroup(g)}
        className="hover:bg-accent flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm font-medium"
      >
        <DepthGuides depth={depth} />
        <CheckboxVisual checked={all} indeterminate={count > 0 && !all} />
        <span className="truncate">{g.name}</span>
        <span className="text-muted-foreground ml-auto shrink-0 text-xs font-normal tabular-nums">
          {ids.length}
        </span>
      </button>
    );
  };

  const groupTree = (list: MultiSelectGroup[], depth: number, path: string): JSX.Element[] =>
    list.flatMap((g) => {
      const key = `${path}/${g.id}`;
      const below =
        g.children && g.children.length > 0
          ? groupTree(g.children, depth + 1, key)
          : groupIds(g).flatMap((id) => {
              const opt = optionById.get(id);
              return opt ? [optionRow(opt, depth + 1, `${key}/${id}`)] : [];
            });
      return [groupRow(g, depth, key), ...below];
    });

  const groupedList = (): JSX.Element[] => {
    if (!groups) return [];
    if (searching) {
      return [
        ...matchedGroups.map((g) => groupRow(g, 0, `match/${g.id}`)),
        ...filtered.map((o) => optionRow(o)),
      ];
    }
    return [
      ...groupTree(groups, 0, 'tree'),
      ...(ungrouped.length > 0 && ungroupedLabel
        ? [
            <p key="ungrouped" className="text-muted-foreground px-3 pb-1 pt-2 text-xs font-medium">
              {ungroupedLabel}
            </p>,
          ]
        : []),
      ...ungrouped.map((o) => optionRow(o, ungroupedLabel ? 1 : 0)),
    ];
  };

  const rows = groups ? groupedList() : filtered.map((opt) => optionRow(opt));

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="flex items-center gap-1">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            // `input-glass` → az Input-mezővel EGYSÉGES keret/felület + a közös halvány zöld focus-ring.
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(
              'input-glass min-w-0 flex-1 justify-between font-normal',
              lowercaseOptions === true && 'lowercase',
            )}
          >
            <span
              className={cn(
                'truncate',
                selectedNames.length === 0 && 'text-muted-foreground',
                selectedNames.length === 0 && lowercasePlaceholder === true && 'lowercase',
              )}
            >
              {triggerLabel}
            </span>
            <span className="ml-2 flex shrink-0 items-center gap-1.5">
              {/* A darabszám CSAK 2+ kijelölésnél: egy elemnél a NÉV már elmondja („Aktív · 1 kiválasztva"
                  redundáns volt — a bevétel-előrejelzés szűrőin mérve). */}
              {selectedNames.length > 1 && countLabel && (
                <span className="text-muted-foreground text-xs tabular-nums">
                  {countLabel(selectedNames.length)}
                </span>
              )}
              <ChevronUpDownIcon className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        {value.length > 0 && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('common.clear')}
            onClick={() => onChange([])}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
      {/* portal={false}: modális Sheet/Dialog belsejében görgethető lista (lásd EntityCombobox). */}
      <PopoverContent align="start" portal={false} className="w-72 p-0">
        {/* Nincs elválasztó vonal a kereső alatt (a rendszer nem használ divider-t). */}
        <div className="p-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // ENTER a keresőben NEM küldi be a környező űrlapot (a kereső a sheet `<form>`-jában ül —
            // kolléga-észrevétel), és nem is pipál: több-választásnál egy véletlen Enter nem kívánt elemet jelölne
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder={t('common.searchPlaceholder')}
            aria-label={t('common.searchPlaceholder')}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">{t('common.noResults')}</p>
          ) : (
            rows
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
